/**
 * Export destinations.
 *
 * mediabunny's `StreamTarget` *closes* its writable when an output is cancelled. For a
 * File System Access `FileSystemWritableFileStream`, closing commits the (partial) file. The
 * guarded stream below therefore forwards writes but only closes the destination after the export
 * succeeded; on cancellation or error it aborts the destination, which discards the partial file.
 */
import type { StreamTargetChunk } from 'mediabunny';

export type ExportDestination =
  /**
   * Stream the MP4 to a writable that accepts positioned writes (`{ type: 'write', position,
   * data }`), e.g. a `FileSystemWritableFileStream` from `FileSystemFileHandle.createWritable()`.
   * Memory use stays constant regardless of the export length.
   */
  | { kind: 'stream'; writable: WritableStream<StreamTargetChunk> }
  /** Keep the whole file in memory and return it as a Blob (small exports / tests only). */
  | { kind: 'memory' };

export interface GuardedStream {
  /** Pass to mediabunny's StreamTarget. */
  readonly stream: WritableStream<StreamTargetChunk>;
  /** Allows the next close (from finalize) to commit the destination. */
  allowCommit(): void;
  /** Aborts the destination unless it has already been committed. Safe to call repeatedly. */
  abort(reason?: unknown): Promise<void>;
  /** Size of the written file (highest written byte + 1). */
  readonly bytesWritten: number;
  readonly committed: boolean;
}

export function guardStream(destination: WritableStream<StreamTargetChunk>): GuardedStream {
  const writer = destination.getWriter();
  let commitAllowed = false;
  let settled: 'committed' | 'aborted' | null = null;
  let bytes = 0;

  const abort = async (reason?: unknown) => {
    if (settled) return;
    settled = 'aborted';
    await writer.abort(reason).catch(() => undefined);
  };

  const stream = new WritableStream<StreamTargetChunk>({
    async write(chunk) {
      bytes = Math.max(bytes, chunk.position + chunk.data.byteLength);
      await writer.ready;
      await writer.write(chunk);
    },
    async close() {
      if (!commitAllowed) {
        await abort(new DOMException('Export was cancelled', 'AbortError'));
        return;
      }
      if (settled) return;
      await writer.close();
      settled = 'committed';
    },
    async abort(reason) {
      await abort(reason);
    },
  });

  return {
    stream,
    allowCommit() {
      commitAllowed = true;
    },
    abort,
    get bytesWritten() {
      return bytes;
    },
    get committed() {
      return settled === 'committed';
    },
  };
}
